import { useCart } from '@/context/cartContext';
import { Check } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { ShippingConfig } from '../type/types';
import { useCleverTap } from '@/context/clevertapContext';
import { getCookie } from 'cookies-next';
import { storage } from '@/config';

const shippingRates = [
  {
    order_start_value: '2500',
    order_end_value: '999999999',
    new_price: '0',
    repeated_price: '0',
    guest_price: '0',
  },
  {
    order_start_value: '0',
    order_end_value: '2499',
    new_price: '99',
    repeated_price: '99',
    guest_price: '99',
  },
];

interface IDeliveryProgress {
  custom_shipping_rates2?: ShippingConfig['custom_shipping_rates'];
}

const DeliveryProgress: React.FC<IDeliveryProgress> = ({
  custom_shipping_rates2,
}) => {
  const [custom_shipping_rates, set_custom_shipping_rates] = useState<
    ShippingConfig['custom_shipping_rates']
  >([]);
  const isGuest = !storage.get('token');

  useEffect(() => {
    const updateMovFromCookies = async () => {
      const movCookie = await getCookie('mov');
      if (movCookie) {
        const parsedMov = JSON.parse(movCookie);
        set_custom_shipping_rates(
          isGuest
            ? shippingRates
            : custom_shipping_rates2 && custom_shipping_rates2?.length > 0
              ? custom_shipping_rates2
              : parsedMov?.custom_shipping_rates
        );
      }
    };
    updateMovFromCookies();
  }, [isGuest, custom_shipping_rates2]);

  const { cartValue, movAmount = 0 } = useCart();
  const { pushEvent } = useCleverTap();
  const lastBroadcastedStateRef = useRef<
    'BELOW_MOV' | 'MOV_ACHIEVED' | 'FREE_SHIPPING_UNLOCKED'
  >('BELOW_MOV');

  const rate = custom_shipping_rates?.find(
    customRate =>
      customRate.new_price === '0' && customRate.repeated_price === '0'
  );
  const freeShippingAmount = rate ? Number(rate.order_start_value) : 0;

  const stages = [
    { amount: movAmount, label: `₹${movAmount}+`, key: 'home' },
    {
      amount: freeShippingAmount,
      label: `₹${freeShippingAmount}`,
      key: 'free',
    },
  ];

  const hasReachedMOV = cartValue >= movAmount;
  const hasFreeShipping = cartValue >= freeShippingAmount;

  const HomeDeliveryEnabled = hasReachedMOV && !hasFreeShipping;
  const FreeShippingEnabled = hasFreeShipping;

  useEffect(() => {
    const currentState = hasFreeShipping
      ? 'FREE_SHIPPING_UNLOCKED'
      : hasReachedMOV
        ? 'MOV_ACHIEVED'
        : 'BELOW_MOV';

    if (currentState !== lastBroadcastedStateRef.current) {
      if (currentState === 'MOV_ACHIEVED') {
        pushEvent({
          event: 'Cart - Progress Bar',
          data: {
            'Online Order Threshold': 'Achieved',
          },
        });
      } else if (currentState === 'FREE_SHIPPING_UNLOCKED') {
        pushEvent({
          event: 'Cart - Progress Bar',
          data: {
            'Free Shipping': 'Unlocked',
          },
        });
      }
      lastBroadcastedStateRef.current = currentState;
    }
  }, [hasReachedMOV, hasFreeShipping, pushEvent]);

  const Text = FreeShippingEnabled ? (
    <b className='font-medium'>
      You’ve Unlocked{' '}
      <b className='text-[#337052] font-semibold'>Free Shipping!</b>
    </b>
  ) : HomeDeliveryEnabled ? (
    <>
      Add{' '}
      <b className='font-semibold'>
        ₹{Math.max(freeShippingAmount - cartValue, 0)}
      </b>{' '}
      more to unlock <b className='font-semibold'>Free Shipping</b>
    </>
  ) : (
    <>
      Add <b className='font-semibold'>₹{movAmount - cartValue}</b> more to get{' '}
      <b className='font-semibold'>home delivery</b> or switch to pickup
    </>
  );
  const pointerValue =
    (cartValue <= (movAmount || 0) ? movAmount : freeShippingAmount) /
    freeShippingAmount;
  const rawProgress = (cartValue / freeShippingAmount) * 100;
  const firstMilestoneProgress = (movAmount / freeShippingAmount) * 100;

  const progressPercent = Math.min(rawProgress, 100);
  const stateConfig = FreeShippingEnabled
    ? {
        bg: 'url(/images/free-shipping-bg/free-ship.svg)',
        color: '#E0EAE5',
      }
    : HomeDeliveryEnabled
      ? {
          bg: 'url(/images/free-shipping-bg/home-dlvry.svg)',
          color: '#FBEFE2',
        }
      : {
          bg: 'url(/images/free-shipping-bg/cartvalue.svg)',
          color: '#F1DFE0',
        };

  return (
    <div
      style={{
        backgroundImage: stateConfig.bg,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'bottom center',
      }}
      className='w-full bg-[#FFFFFF] flex flex-col items-center pb-6'>
      {/* Tooltip Wrapper */}
      <div className='w-full relative mb-1.5'>
        <div
          style={{
            backgroundColor: stateConfig.color,
          }}
          className='capitalize text-[#39393B] transition-all w-full text-xs md:text-sm font-medium leading-[15px] duration-500 ease-out py-[11.5px] px-4 bg-[#F1DFE0] border whitespace-nowrap font-gilroy-fallback'>
          {Text}
          {/* Tooltip Arrow */}
          <div
            style={{
              left: `calc(33px + (100% - 66px) * (${pointerValue * 100} / 100))`,
              backgroundColor: stateConfig.color,
            }}
            className='absolute left-1/2 -translate-x-1/2 rounded-xs translate-y-2/3 w-2.5 h-2.5 bg-[#F1DFE0] border-r border-b rotate-45'></div>
        </div>
      </div>

      <div className='w-[85%] md:w-[85%] relative py-2'>
        {/* Progress Track */}
        <div className='h-1 w-[calc(100%+30px)] -mx-[15px] bg-[#F5F5F6] rounded-full overflow-hidden relative'>
          {/* Solid fill till first milestone */}
          {cartValue >= movAmount && (
            <div
              className='absolute h-full bg-[#39393B] rounded-full z-1 transition-all duration-700 ease-out'
              style={{ width: `${firstMilestoneProgress}%` }}
            />
          )}

          {/* Gradient fill beyond milestone */}
          <div
            className='absolute h-full left-0 rounded-full transition-all duration-700 ease-out'
            style={{
              width: FreeShippingEnabled ? '100%' : `${progressPercent}%`,
              background: FreeShippingEnabled
                ? '#39393B'
                : cartValue >= movAmount
                  ? `linear-gradient(
            90deg,
            #39393B 0%,
            #39393B ${firstMilestoneProgress}%,
            #39393B ${firstMilestoneProgress}%,
            #F5F5F6 100%
          )`
                  : `linear-gradient(90deg, #39393B 0%, #F5F5F6 100%)`,
            }}
          />
        </div>

        {/* Milestones */}
        {stages.map(stage => {
          const stagePercent = (stage?.amount / freeShippingAmount) * 100;
          const isReached = cartValue >= stage?.amount;
          return (
            <div
              key={stage.key}
              className='absolute top-1/2 -translate-y-1/2 -translate-x-1/2 flex flex-col items-center z-1'
              style={{ left: `${stagePercent}%` }}>
              {/* Milestone Dot */}
              {isReached ? <ActiveDot /> : <InactiveDot />}
              {/* Milestone Label */}
              <span
                className={`absolute top-4 text-xs md:text-sm whitespace-nowrap transition-colors duration-300 ${
                  isReached ? 'text-[#39393B]' : 'text-[#85868B]'
                }`}>
                {stage.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DeliveryProgress;

const ActiveDot = () => (
  <div className='size-[13px] ring-3 ring-[#FFFFFF] rounded-full flex items-center justify-center'>
    <div className='size-[13px] bg-[#39393B] rounded-full flex items-center justify-center'>
      <Check
        strokeWidth={3}
        size={10}
        className='text-[10px]'
        color='#ffffff'
      />
    </div>
  </div>
);

const InactiveDot = () => (
  <div className='flex items-center'>
    <div className='flex size-3 items-center justify-center rounded-full bg-white'>
      <div
        className={`
                    flex size-3 items-center justify-center rounded-full transition-all
                    ring-1 ring-[#CFCFD2]
                  `}>
        <div className={`size-1.5 rounded-full bg-[#CFCFD2]`} />
      </div>
    </div>
  </div>
);
